import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { BodyPositionDisplay } from '../Processed/BodyPositionDisplay';
import styles from './OnlineDataPanel.module.scss';

const ONLINE_STATE_NO_ONLINE = 0;
const ONLINE_STATE_WAITING = 10;
const ONLINE_STATE_RECEIVING = 20;
const ONLINE_STATE_FINISHED = 30;

export function OnlineDataPanel({ processedData, onlineTransmissionState, onScrollToEndCallback }) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [animateIcon, setAnimateIcon] = useState(false);
  
  useEffect(() => {
    if (onlineTransmissionState === ONLINE_STATE_RECEIVING) {
      setAnimateIcon(true);
    } else {
      setAnimateIcon(false);
    }
  }, [onlineTransmissionState]);

  const getStatusIcon = () => {
    switch(onlineTransmissionState) {
      case ONLINE_STATE_WAITING:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" className={styles.statusIcon}>
            <circle cx="12" cy="12" r="10" fill="none" stroke="orange" strokeWidth="2">
              <animate attributeName="stroke-opacity" 
                values="0;1;0" 
                dur="3s" 
                repeatCount="indefinite"/>
            </circle>
            <circle cx="12" cy="12" r="5" fill="orange">
              <animate attributeName="opacity" 
                values="0;1;0" 
                dur="3s" 
                repeatCount="indefinite"/>
            </circle>
          </svg>
        );
      case ONLINE_STATE_RECEIVING:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" className={styles.statusIcon}>
            <path d="M 2 12 L 6 12 L 8 8 L 10 16 L 12 4 L 14 20 L 16 12 L 18 12 L 22 12" 
              stroke="limegreen" 
              strokeWidth="2" 
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round">
              <animate attributeName="stroke-opacity" 
                values="0;1;0" 
                dur="3s" 
                repeatCount="indefinite"/>
            </path>
          </svg>
        );
      case ONLINE_STATE_FINISHED:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" className={styles.statusIcon}>
            <circle cx="12" cy="12" r="10" fill="none" stroke="#999" strokeWidth="2"/>
            <path d="M8 12 L16 12" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getButtonTooltip = () => {
    switch(onlineTransmissionState) {
      case ONLINE_STATE_WAITING:
        return t('recordView.realtime.state.waiting');
      case ONLINE_STATE_RECEIVING:
        return t('recordView.realtime.state.live');
      case ONLINE_STATE_FINISHED:
        return t('recordView.realtime.state.offline');
      default:
        return '';
    }
  };

  return (
    <>
      <button 
        className={styles.toggleButton}
        onClick={() => setIsVisible(!isVisible)}
        title={getButtonTooltip()}
        aria-label="Toggle online data panel"
      >
        {getStatusIcon()}
      </button>

      {isVisible && (
        <div className={styles.dataPanel}>
          <div className={styles.statusRow}>
            <div className={styles.statusIndicator}>
              {onlineTransmissionState === ONLINE_STATE_WAITING && (
                <>
                  <i className="bi bi-circle-fill" style={{ color: 'orange' }}></i>
                  <span className={styles.statusText}>{t('recordView.realtime.state.waiting')}</span>
                </>
              )}
              {onlineTransmissionState === ONLINE_STATE_RECEIVING && (
                <>
                  <i className="bi bi-circle-fill" style={{ color: 'limegreen' }}></i>
                  <span className={styles.statusText}>{t('recordView.realtime.state.live')}</span>
                </>
              )}
              {onlineTransmissionState === ONLINE_STATE_FINISHED && (
                <span className={styles.statusText}>{t('recordView.realtime.state.offline')}</span>
              )}
            </div>
          </div>

          <BodyPositionDisplay 
            bodyPosition={processedData?.bodyPosition} 
            className={styles.bodyPositionIcon}
            wrapperClassName={styles.bodyPositionRow}
          />

          <div className={styles.dataRow}>
            <span className={styles.label}>HR:</span>
            <span className={styles.value}>
              {processedData?.heartRateValue ? `${processedData.heartRateValue} bpm` : '-'}
            </span>
          </div>

          <div className={styles.dataRow}>
            <span className={styles.label}>HRV:</span>
            <span className={styles.value}>
              {processedData?.hrv != null ? `${Math.round(processedData.hrv)} ms` : '-'}
            </span>
          </div>

          <div className={styles.dataRow}>
            <span className={styles.label}>SI:</span>
            <span className={styles.value}>{processedData?.si ?? '-'}</span>
          </div>

          <div className={styles.dataRow}>
            <span className={styles.label}>BrR:</span>
            <span className={styles.value}>{processedData?.breath ?? '-'}</span>
          </div>

          <div className={styles.dataRow}>
            <span className={styles.label}>Apnoe:</span>
            <span className={styles.value}>{processedData?.apnoe ?? '-'}</span>
          </div>

          <div className={styles.dataRow}>
            <span className={styles.label}>BoT:</span>
            <span className={styles.value}>
              {processedData?.temperature ? `${processedData.temperature}°C` : '-'}
            </span>
          </div>

          {(onlineTransmissionState === ONLINE_STATE_RECEIVING || onlineTransmissionState === ONLINE_STATE_WAITING) && (
            <div className={styles.buttonRow}>
              <Button
                variant="outline-primary"
                onClick={onScrollToEndCallback}
                style={{ width: '100%', marginTop: '8px' }}
              >
                {t('recordView.realtime.state.scrollToEnd')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}